import MuiAlert from '@mui/material/Alert';
import WarningIcon from '@mui/icons-material/Warning';
import { forwardRef, type ReactNode } from 'react';

export interface AlertProps {
  severity?: 'error' | 'warning' | 'info' | 'success';
  // `notice`: white surface, hairline frame, solid full-height colour block, bold message.
  // `filled`: solid surface, white bold message — the panel's floating notice (ADR 0021).
  // `standard` (MUI's tinted strip) stays the default so existing callers are unaffected.
  variant?: 'standard' | 'notice' | 'filled';
  children?: ReactNode;
  // Controls in MUI's action slot; `notice` already styles it.
  // MUI renders `action` INSTEAD of the close button — passing both drops the «X».
  action?: ReactNode;
  // When set, the alert renders a dismiss «X»; `closeText` labels it (aria-label).
  onClose?: () => void;
  closeText?: string;
}

// Only warning is overridden — MUI's other default glyphs already match.
const NOTICE_ICON_MAPPING = { warning: <WarningIcon fontSize="inherit" /> };

// Styled over MUI's own slots, never rebuilt from primitives. Two non-obvious constraints:
// `overflow: hidden` keeps the square colour block inside the rounded frame, and zeroing the root
// padding forces the icon, message and action slots to each pay their own.
const noticeSx = (severity: NonNullable<AlertProps['severity']>) => ({
  p: 0,
  overflow: 'hidden',
  alignItems: 'stretch',
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  // sx resolves `2` as shape.borderRadius × 2 = 8px.
  borderRadius: 2,
  '& .MuiAlert-icon': {
    m: 0,
    px: 1.5,
    alignItems: 'center',
    opacity: 1,
    color: 'common.white',
    bgcolor: `${severity}.main`,
  },
  '& .MuiAlert-message': {
    px: 2,
    py: 1.25,
    color: 'text.primary',
    fontWeight: 700,
  },
  '& .MuiAlert-action': {
    // Three of the four margins go, but NOT `marginLeft: auto` — that is what pins the action to
    // the trailing edge, and the message slot has no flex-grow to do it instead. Zeroing all four
    // drops the control next to the text, where it reads as part of the sentence.
    ml: 'auto',
    mr: 0,
    my: 0,
    pr: 1,
    pt: 0,
    alignItems: 'center',
  },
});

// The only variant of this façade that maps onto a MUI one. MUI's filled already paints the icon,
// the message and the «X» white; what it gets wrong for the panel is the SURFACE, `<severity>.main`
// — 3.09:1 on white for error, which `TextField.tsx` already rules out for colour that carries
// meaning in text. `.dark` is 5.77:1 and the theme derives it, so no token is added. Bold is ours:
// in a floating notice the message is the whole content.
const filledSx = (severity: NonNullable<AlertProps['severity']>) => ({
  bgcolor: `${severity}.dark`,
  '& .MuiAlert-message': { fontWeight: 700 },
});

// forwardRef: a MUI transition mounts its child by reading layout off its DOM node, so the floating
// notice's Alert has to hand it over or `Grow` throws on a null node. Mirrors Button and IconButton.
export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    severity = 'info',
    variant = 'standard',
    children,
    action,
    onClose,
    closeText = 'Cerrar', // TODO: i18n
  },
  ref,
) {
  const notice = variant === 'notice';
  const filled = variant === 'filled';
  return (
    <MuiAlert
      ref={ref}
      severity={severity}
      closeText={closeText}
      {...(notice ? { iconMapping: NOTICE_ICON_MAPPING, sx: noticeSx(severity) } : {})}
      {...(filled ? { variant: 'filled' as const, sx: filledSx(severity) } : {})}
      {...(action ? { action } : {})}
      // Omit the handler when unset: exactOptionalPropertyTypes rejects onClose={undefined}.
      {...(onClose ? { onClose } : {})}
    >
      {children}
    </MuiAlert>
  );
});
